import type { Cursor } from './contract.js';

/**
 * LE CURSEUR DE FICHE (docs/VUES-CONTRAT.md, section 5).
 *
 * Le principe tient en une phrase : la liste d'ids est GELEE a l'ouverture de
 * la fiche. "3 / 47" ne bouge pas sous les doigts, meme si l'utilisateur
 * change la recherche pendant qu'il lit. Les filtres et le tri restent actifs
 * et pilotent le PROCHAIN curseur, pas celui en cours.
 *
 * Deux consequences tenues ici :
 *
 * - AUCUNE SUBSTITUTION SILENCIEUSE. Si l'element ouvert sort du jeu courant
 *   (filtre change, element modifie par un collegue), on ne le remplace pas
 *   par son voisin : on le garde affiche et on leve `outOfScope`, a charge de
 *   l'ecran d'afficher "hors du filtre courant". Un `rows[i] ?? rows[0]` fait
 *   croire qu'on edite A quand on edite B.
 *
 * - ENTREE A FROID. Reprise de session ou lien profond : la fiche s'affiche
 *   avant que la collection soit la. Tant que le rang n'est pas resolu,
 *   `ready` est faux, le compteur et les fleches sont desactives. On ne charge
 *   pas 10 000 lignes pour afficher une fiche.
 *
 * Fonctions PURES : l'etat (la liste gelee) vit dans `useScreen`, pas ici.
 */

/** Curseur neutre : rien d'ouvert, rien de gele. */
export const EMPTY_CURSOR: Cursor = {
  ids: [],
  openId: null,
  index: -1,
  total: 0,
  ready: false,
  outOfScope: false,
  hasPrev: false,
  hasNext: false,
  prevId: null,
  nextId: null,
};

/**
 * Gele un jeu ordonne d'ids. Copie defensive : le tableau source continue de
 * vivre (tri, filtre, rechargement) sans deformer le curseur en cours.
 */
export function freezeIds(ids: readonly string[]): string[] {
  return [...ids];
}

/**
 * Construit le curseur a partir de la liste GELEE, du jeu ordonne COURANT et
 * de l'element ouvert.
 *
 * @param frozenIds  la liste gelee a l'ouverture, `null` si le gel n'a pas
 *                   encore pu avoir lieu (entree a froid : collection absente).
 * @param currentIds le jeu ordonne courant, qui sert UNIQUEMENT a detecter la
 *                   sortie du filtre. Il ne pilote jamais precedent / suivant.
 * @param openId     l'element ouvert, `null` hors fiche.
 */
export function buildCursor(
  frozenIds: readonly string[] | null,
  currentIds: readonly string[],
  openId: string | null,
): Cursor {
  if (openId == null) {
    return { ...EMPTY_CURSOR, ids: frozenIds ? [...frozenIds] : [] };
  }

  const ids = frozenIds ? [...frozenIds] : [];
  const index = frozenIds ? ids.indexOf(openId) : -1;
  const ready = index >= 0;
  const total = ids.length;

  return {
    ids,
    openId,
    index,
    total,
    ready,
    // Sorti du filtre : l'element n'est plus dans le jeu courant. Sans
    // collection chargee (currentIds vide), on ne conclut rien.
    outOfScope: currentIds.length > 0 && !currentIds.includes(openId),
    hasPrev: ready && index > 0,
    hasNext: ready && index < total - 1,
    prevId: ready && index > 0 ? (ids[index - 1] ?? null) : null,
    nextId: ready && index < total - 1 ? (ids[index + 1] ?? null) : null,
  };
}

/**
 * Le premier element du jeu ordonne courant : ce qu'on ouvre quand
 * l'utilisateur clique "Fiche" sans rien avoir selectionne. C'est une entree
 * legitime dans la fiche, pas un accident, donc pas d'etat d'erreur.
 */
export function firstOf(currentIds: readonly string[]): string | null {
  return currentIds[0] ?? null;
}
